// components/common/SafeChart.js
import React, { useEffect, useState, useRef } from "react";
import ECharts from "./ECharts";

// Error Boundary 來捕捉 React 渲染錯誤
class ChartErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Chart Error Boundary caught an error:", error, errorInfo);
    this.setState({ error });

    // 通知父組件有錯誤發生
    if (this.props.onError) {
      this.props.onError(error);
    }
  }

  componentDidUpdate(prevProps) {
    // 當 props 改變時重置錯誤狀態
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false, error: null });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="h-full flex items-center justify-center bg-gray-800 rounded-[16px]">
          <div className="text-center text-gray-400">
            <div className="text-2xl mb-2">⚠️</div>
            <div className="text-lg mb-2">圖表渲染錯誤</div>
            <div className="text-sm mb-3 text-gray-500">
              {this.props.showErrorDetails
                ? this.state.error?.message
                : "ECharts 內部發生錯誤"}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// 圓餅圖顯示百分比
const defaultPieLabelFormatter = ({ name, percent }) => {
  if (Number.isFinite(percent)) {
    const rounded = percent % 1 === 0 ? percent : percent.toFixed(1);
    return `${name}\n${rounded}%`;
  }
  return `${name}\n${percent || "0"}%`;
};

const applyPieDefaults = (option) => {
  if (!option || typeof option !== "object") {
    return option;
  }

  let hasPieSeries = false;
  const enhancedOption = { ...option };

  if (Array.isArray(option.series)) {
    enhancedOption.series = option.series.map((seriesItem) => {
      if (!seriesItem || seriesItem.type !== "pie") {
        return seriesItem;
      }

      hasPieSeries = true;
      const defaultLabel = {
        show: true,
        formatter: defaultPieLabelFormatter,
        overflow: "break",
        lineHeight: 18,
      };
      const mergedLabel = {
        ...defaultLabel,
        ...seriesItem.label,
      };

      if (seriesItem.label?.formatter) {
        mergedLabel.formatter = seriesItem.label.formatter;
      }

      return {
        ...seriesItem,
        label: mergedLabel,
        labelLine: {
          show: true,
          smooth: true,
          length: 15,
          length2: 10,
          ...seriesItem.labelLine,
        },
      };
    });
  }

  if (hasPieSeries) {
    const pieTooltipDefaults = {
      trigger: "item",
      formatter: (params) => {
        const value = Number(params.value);
        const formattedValue = Number.isFinite(value) ? value.toFixed(2) : params.value;
        return `${params.name}: ${formattedValue} kW (${params.percent}%)`;
      },
    };

    enhancedOption.tooltip = enhancedOption.tooltip
      ? { ...pieTooltipDefaults, ...enhancedOption.tooltip }
      : pieTooltipDefaults;
  }

  return enhancedOption;
};
const SafeChart = ({
  data,
  title,
  className = "h-[600px] w-full",
  showTitle = true,
}) => {
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [resetKey, setResetKey] = useState(0);
  const errorHandlerRef = useRef(null);

  useEffect(() => {
    // 當資料改變時重置錯誤狀態
    setHasError(false);
    setErrorMessage("");
    setResetKey((prev) => prev + 1);
  }, [data]);

  useEffect(() => {
    // 全域錯誤處理器，捕捉 ECharts 的異步錯誤
    const handleError = (event) => {
      // 檢查是否是 ECharts 相關錯誤
      const error = event.error || event.reason;
      if (
        error &&
        (error.message?.includes("ECharts") ||
          error.message?.includes("Graph") ||
          error.message?.includes("duplicate") ||
          error.stack?.includes("echarts") ||
          error.stack?.includes("Graph.js") ||
          event.filename?.includes("echarts"))
      ) {
        console.error("Caught ECharts async error:", error);
        setHasError(true);
        setErrorMessage(error.message || "圖表渲染錯誤");
        event.preventDefault(); // 防止錯誤冒泡
        event.stopPropagation();
        return false;
      }
    };

    const handleUnhandledRejection = (event) => {
      const error = event.reason;
      if (
        error &&
        (error.message?.includes("ECharts") ||
          error.message?.includes("Graph") ||
          error.stack?.includes("echarts"))
      ) {
        console.error("Caught ECharts promise rejection:", error);
        setHasError(true);
        setErrorMessage(error.message || "圖表載入錯誤");
        event.preventDefault();
        return false;
      }
    };

    // 綁定全域錯誤處理器
    window.addEventListener("error", handleError, true);
    window.addEventListener(
      "unhandledrejection",
      handleUnhandledRejection,
      true
    );

    errorHandlerRef.current = { handleError, handleUnhandledRejection };

    return () => {
      if (errorHandlerRef.current) {
        window.removeEventListener(
          "error",
          errorHandlerRef.current.handleError,
          true
        );
        window.removeEventListener(
          "unhandledrejection",
          errorHandlerRef.current.handleUnhandledRejection,
          true
        );
      }
    };
  }, []);
  const handleBoundaryError = (error) => {
    console.error("Error boundary caught:", error);
    setHasError(true);
    setErrorMessage(error.message || "圖表組件錯誤");
  };

  if (hasError) {
    return (
      <div
        className={`${className} flex items-center justify-center bg-gray-800 rounded-[16px]`}
      >
        <div className="text-center text-gray-400">
          <div className="text-2xl mb-2">📊</div>
          <div className="text-lg mb-2">圖表載入失敗</div>
          <div className="text-sm mb-3 text-gray-500">
            {errorMessage || "資料格式錯誤或圖表配置有問題"}
          </div>
        </div>
      </div>
    );
  }

  // 資料驗證
  try {
    let chartOption;

    if (!data) {
      setHasError(true);
      setErrorMessage("圖表資料為空");
      return null;
    }

    if (data?.option) {
      // 如果 data 有 option 屬性，使用 data.option
      chartOption = data.option;
    } else if (data && typeof data === "object" && !Array.isArray(data)) {
      // 如果 data 本身就是 option 物件，直接使用 data
      // 檢查是否包含 ECharts 的基本結構 (series, title, xAxis, yAxis 等)
      if (
        data.series ||
        data.title ||
        data.xAxis ||
        data.yAxis ||
        data.legend
      ) {
        chartOption = data;
      } else {
        setHasError(true);
        setErrorMessage("無效的圖表資料格式");
        return null;
      }
    } else {
      setHasError(true);
      setErrorMessage("無效的圖表資料格式");
      return null;
    }

    // 基本的圖表配置檢查
    if (!chartOption || typeof chartOption !== "object") {
      setHasError(true);
      setErrorMessage("圖表配置物件無效");
      return null;
    }

    // 檢查常見的錯誤配置
    if (chartOption.series) {
      const series = chartOption.series;
      if (Array.isArray(series)) {
        for (let i = 0; i < series.length; i++) {
          const s = series[i];
          // 檢查 Graph 圖的常見錯誤
          if (s.type === "graph") {
            if (s.data && Array.isArray(s.data)) {
              // 檢查重複的 name 或 id
              const ids = s.data.map((item) => item.id).filter(Boolean);
              const names = s.data.map((item) => item.name).filter(Boolean);

              // 檢查 id 重複
              if (ids.length > 0) {
                const uniqueIds = new Set(ids);
                if (ids.length !== uniqueIds.size) {
                  setHasError(true);
                  setErrorMessage("Graph 圖表資料包含重複的節點 ID");
                  console.log("重複的 ID:", ids);
                  return null;
                }
              }

              // 檢查 name 重複
              if (names.length > 0) {
                const uniqueNames = new Set(names);
                if (names.length !== uniqueNames.size) {
                  setHasError(true);
                  setErrorMessage("Graph 圖表資料包含重複的節點名稱");
                  console.log("重複的名稱:", names);
                  return null;
                }
              }

              // 檢查 links 是否有效
              if (s.links && Array.isArray(s.links)) {
                const nodeIds = new Set(
                  s.data.map((item) => item.id || item.name)
                );
                for (let j = 0; j < s.links.length; j++) {
                  const link = s.links[j];
                  if (!link.source || !link.target) {
                    setHasError(true);
                    setErrorMessage(`第 ${j + 1} 個連結缺少 source 或 target`);
                    return null;
                  }

                  if (!nodeIds.has(link.source) || !nodeIds.has(link.target)) {
                    console.warn(
                      `連結 ${link.source} -> ${link.target} 的節點不存在於資料中`
                    );
                    console.log("現有節點:", Array.from(nodeIds));
                    console.log("問題連結:", link);
                    // 這裡不直接返回錯誤，因為有些情況下 ECharts 可能會自動處理
                  }
                }
              }
            }
          }
          // 檢查 Sankey 圖的常見錯誤
          else if (s.type === "sankey") {
            if (s.data && Array.isArray(s.data)) {
              const names = s.data.map((item) => item.name).filter(Boolean);
              const uniqueNames = new Set(names);
              if (names.length !== uniqueNames.size) {
                setHasError(true);
                setErrorMessage("Sankey 圖表資料包含重複的節點名稱");
                return null;
              }
            }
          }
        }
      }
    }

    const optionWithPieDefaults = applyPieDefaults(chartOption);

    return (
      <ChartErrorBoundary
        resetKey={resetKey}
        onError={handleBoundaryError}
        showErrorDetails={process.env.NODE_ENV === "development"}
      >
        <ECharts option={optionWithPieDefaults} theme="dark" />
      </ChartErrorBoundary>
    );
  } catch (error) {
    console.error("Chart validation error:", error);
    console.log("問題資料:", data);
    setHasError(true);
    setErrorMessage(error.message || "圖表驗證失敗");
    return null;
  }
};

// components/common/ChartSection.js
const ChartSection = ({
  data,
  title,
  className = "h-[600px] w-full flex flex-col gap-4",
  titleClassName = "flex rounded-[16px] w-fit items-center justify-center p-2 px-4 bg-[#388296] border border-[#575757] text-white font-bold text-[20px]",
  showTitle = true,
}) => {
  return (
    <div className={className}>
      {showTitle && (
        <div className={titleClassName}>
          {title || data?.option?.title || "圖表"}
        </div>
      )}
      <SafeChart data={data} />
    </div>
  );
};

// components/common/ChartContainer.js
const ChartContainer = ({
  children,
  className = "flex flex-col lg:flex-row gap-4 w-full bg-gradient-to-r from-[#D9D9D933] to-[#73737333] rounded-[16px] p-6",
}) => {
  return <div className={className}>{children}</div>;
};

// components/common/MultiChart.js - 多圖表組合組件
const MultiChart = ({
  charts = [],
  layout = "horizontal", // "horizontal", "vertical", "grid"
  containerClassName,
}) => {
  const getLayoutClass = () => {
    switch (layout) {
      case "vertical":
        return "flex flex-col gap-4 w-full";
      case "grid":
        return "grid grid-cols-1 lg:grid-cols-2 gap-4 w-full";
      default: // horizontal
        return "flex flex-col lg:flex-row gap-4 w-full";
    }
  };

  const defaultContainerClass = `${getLayoutClass()} bg-gradient-to-r from-[#D9D9D933] to-[#73737333] rounded-[16px] p-6`;

  return (
    <ChartContainer className={containerClassName || defaultContainerClass}>
      {charts.map((chart, index) => (
        <ChartSection
          key={chart.key || index}
          data={chart.data}
          title={chart.title}
          className={chart.className}
          titleClassName={chart.titleClassName}
          showTitle={chart.showTitle !== false}
        />
      ))}
    </ChartContainer>
  );
};

export { SafeChart, ChartSection, ChartContainer, MultiChart };
